/**
 * Supported UI languages for react-intl catalogs. Align this list with Crowdin
 * target languages when the revenue-suite project is created.
 */
export const APP_LANGUAGES = [
  'en',
  'sv',
  'no',
  'da',
  'fi',
  'de',
  'nl',
  'fr',
  'es',
  'pt',
  'it',
] as const;

export type AppLanguage = (typeof APP_LANGUAGES)[number];

export const DEFAULT_LOCALE: AppLanguage = 'en';

const isAppLanguage = (locale: string): locale is AppLanguage =>
  APP_LANGUAGES.includes(locale as AppLanguage);

/**
 * Maps a BCP 47 tag (e.g. `sv-SE`) to a supported two-letter app language.
 */
export const normalizeToAppLanguage = (locale: string): AppLanguage | undefined => {
  const languageCode = locale.toLowerCase().split('-')[0];
  return isAppLanguage(languageCode) ? languageCode : undefined;
};

/**
 * Picks the first navigator language that we ship a catalog for.
 */
export const getAvailableLanguageFromNavigator = (
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages
): AppLanguage => {
  for (const candidate of languages) {
    const appLanguage = normalizeToAppLanguage(candidate);
    if (appLanguage) {
      return appLanguage;
    }
  }
  return DEFAULT_LOCALE;
};

/**
 * Account locale first, then the browser, then English.
 */
export const resolveAppLanguage = (accountLocale: string | null | undefined): AppLanguage => {
  if (accountLocale) {
    const appLanguage = normalizeToAppLanguage(accountLocale);
    if (appLanguage) {
      return appLanguage;
    }
  }
  return getAvailableLanguageFromNavigator();
};
